import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Trash2, UserCheck, UserX, Key, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Şifre sıfırlama state'leri
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Kullanıcı silme state'leri
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePassword2, setDeletePassword2] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1: İlk şifre, 2: Emin misiniz?, 3: İkinci şifre

  const fetchUsers = useCallback(async (showToast = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/auth/users`, { timeout: 15000 });
      setUsers(response.data);
      if (showToast) toast.success('Kullanıcılar yüklendi');
    } catch (err) {
      console.error('Kullanıcılar yüklenemedi:', err);
      setError(err.message || 'Bağlantı hatası');
      toast.error('Kullanıcılar yüklenemedi. Yenile butonuna tıklayın.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/auth/approve/${userId}`);
      toast.success('Kullanıcı onaylandı');
      fetchUsers();
    } catch (err) {
      toast.error('Onaylama başarısız');
    }
  };

  // Silme dialogunu aç
  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeletePassword('');
    setDeletePassword2('');
    setDeleteStep(1);
    setDeleteDialogOpen(true);
  };

  // Dialog kapat
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeletePassword('');
    setDeletePassword2('');
    setDeleteStep(1);
  };

  // Adım 1: İlk şifre doğrulama
  const handleFirstPassword = async () => {
    if (!deletePassword) {
      toast.error('Lütfen şifrenizi girin');
      return;
    }

    try {
      // Şifreyi doğrulamak için login endpoint'ini kullan
      await axios.post(`${API}/auth/login`, {
        email: currentUser.email,
        password: deletePassword
      });
      // Şifre doğru, adım 2'ye geç
      setDeleteStep(2);
    } catch (err) {
      toast.error('Şifre hatalı!');
    }
  };

  // Adım 2: Emin misiniz? - Evet
  const handleConfirmYes = () => {
    setDeleteStep(3);
  };

  // Adım 3: İkinci şifre ve silme
  const handleFinalDelete = async () => {
    if (!deletePassword2) {
      toast.error('Lütfen şifrenizi tekrar girin');
      return;
    }

    try {
      setDeleteLoading(true);
      await axios.delete(`${API}/auth/users/${userToDelete.id}`, {
        data: { password: deletePassword2 }
      });
      toast.success('Kullanıcı ve tüm verileri silindi');
      closeDeleteDialog();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      await axios.post(`${API}/auth/reset-password/${selectedUser.id}?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Şifre başarıyla güncellendi');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre güncellenemedi');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🔐 Kullanıcı Yönetimi
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => fetchUsers(true)}
            disabled={loading}
            className="ml-2"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">
              <XCircle className="w-12 h-12 mx-auto mb-2" />
              <p className="font-semibold">Kullanıcılar yüklenemedi</p>
              <p className="text-sm text-gray-500">{error}</p>
            </div>
            <Button onClick={() => fetchUsers(true)} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tekrar Dene
            </Button>
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Yükleniyor...</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Kayıt Tarihi</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.ad_soyad}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell className="text-sm">{user.telefon || '—'}</TableCell>
                  <TableCell>
                    {user.approved ? (
                      <Badge variant="default" className="bg-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Onaylı
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="w-3 h-3 mr-1" />
                        Beklemede
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <Badge variant="outline" className="border-orange-600 text-orange-600">
                        Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline">Kullanıcı</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(user.created_at).toLocaleDateString('tr-TR')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      {!user.approved && (
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(user.id)}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Onayla
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user);
                          setResetPasswordDialogOpen(true);
                        }}
                        title="Şifre Sıfırla"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser.id && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openDeleteDialog(user)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {users.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            Henüz kullanıcı yok
          </div>
        )}
      </CardContent>

      {/* Şifre Sıfırlama Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔑 Şifre Sıfırla</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-600">Kullanıcı</Label>
              <p className="font-semibold">{selectedUser?.ad_soyad}</p>
              <p className="text-sm text-gray-500">{selectedUser?.email}</p>
            </div>
            <div>
              <Label>Yeni Şifre</Label>
              <Input
                type="text"
                placeholder="En az 6 karakter"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
              />
              <p className="text-xs text-gray-500 mt-1">
                Bu şifreyi kullanıcıya manuel olarak bildirmeyi unutmayın!
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setNewPassword('');
                setSelectedUser(null);
              }}
            >
              İptal
            </Button>
            <Button onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 6}>
              Şifreyi Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kullanıcı Silme Dialog - 3 Adımlı */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Kullanıcıyı Sil {deleteStep > 1 && `(${deleteStep}/3)`}
            </DialogTitle>
          </DialogHeader>

          {/* ADIM 1: İlk Şifre Girişi */}
          {deleteStep === 1 && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium">⚠️ DİKKAT!</p>
                <p className="text-red-700 text-sm mt-1">
                  Bu işlem geri alınamaz! Kullanıcı ve tüm verileri (müşteriler, vergi kayıtları, yüklenen vergiler) kalıcı olarak silinecektir.
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Silinecek Kullanıcı</Label>
                <p className="font-semibold">{userToDelete?.ad_soyad}</p>
                <p className="text-sm text-gray-500">{userToDelete?.email}</p>
              </div>
              <div>
                <Label className="text-red-600 font-medium">1. Adım: Şifrenizi girin</Label>
                <Input
                  type="password"
                  placeholder="Admin şifreniz"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="mt-1"
                  autoComplete="off"
                  onKeyDown={(e) => e.key === 'Enter' && handleFirstPassword()}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>
                  İptal
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleFirstPassword} 
                  disabled={!deletePassword}
                >
                  Devam Et
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* ADIM 2: Emin misiniz? */}
          {deleteStep === 2 && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 text-center">
                <p className="text-yellow-800 font-bold text-lg mb-2">⚠️ EMİN MİSİNİZ?</p>
                <p className="text-yellow-700">
                  <strong>{userToDelete?.ad_soyad}</strong> kullanıcısı ve tüm verileri kalıcı olarak silinecektir!
                </p>
              </div>
              <DialogFooter className="flex gap-2 justify-center">
                <Button 
                  variant="outline" 
                  onClick={closeDeleteDialog}
                  className="flex-1"
                >
                  Hayır, Vazgeç
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleConfirmYes}
                  className="flex-1"
                >
                  Evet, Devam Et
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* ADIM 3: Son Onay - Tekrar Şifre */}
          {deleteStep === 3 && (
            <div className="space-y-4">
              <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-center">
                <p className="text-red-800 font-bold text-lg">🔐 SON ONAY</p>
                <p className="text-red-700 text-sm mt-1">
                  Silme işlemini onaylamak için şifrenizi tekrar girin
                </p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Silinecek Kullanıcı</Label>
                <p className="font-semibold">{userToDelete?.ad_soyad}</p>
                <p className="text-sm text-gray-500">{userToDelete?.email}</p>
              </div>
              <div>
                <Label className="text-red-600 font-medium">3. Adım: Şifrenizi tekrar girin</Label>
                <Input
                  type="password"
                  placeholder="Admin şifreniz (tekrar)"
                  value={deletePassword2}
                  onChange={(e) => setDeletePassword2(e.target.value)}
                  className="mt-1"
                  autoComplete="off"
                  onKeyDown={(e) => e.key === 'Enter' && handleFinalDelete()}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>
                  Vazgeç
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleFinalDelete} 
                  disabled={!deletePassword2 || deleteLoading}
                >
                  {deleteLoading ? 'Siliniyor...' : '🗑️ Kullanıcıyı Sil'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
